/* FAQ · /faq — common questions, answered straight. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";
import MarketingMobileNav from "../components/MarketingMobileNav";

const SECTIONS = [
  {
    label: "For sellers",
    items: [
      {
        q: "How fast do I actually get paid?",
        a: "Same business day. The moment your buyer's order is approved, verqo wires the invoice amount (minus our flat fee) directly to your wallet via ACH or wire — no waiting on Net-30/60/90 terms.",
      },
      {
        q: "Does my buyer know verqo is involved?",
        a: "Only if you want them to. By default, verqo runs invisibly — your buyer still sees your invoice, your branding, and pays you under their existing terms. We just front the cash.",
      },
      {
        q: "What happens if a buyer doesn't pay?",
        a: "verqo absorbs the loss. Every funded invoice is covered by trade-credit insurance underwritten by Allianz Trade. At day 60 past due, insurance takes over — you keep the cash you were paid.",
      },
      {
        q: "How is this different from traditional factoring?",
        a: "Factoring takes 3-7 days, charges hidden reserve fees, and disrupts your buyer relationship because the factor contacts them directly. verqo is same-day, flat-fee, and invisible to the buyer.",
      },
      {
        q: "Can my buyers re-use their verqo credit line with other suppliers?",
        a: "Yes — that's the magic. Each buyer gets one portable credit line that works across every supplier on verqo, which means buyers prefer ordering from sellers on the network.",
      },
      {
        q: "How long does onboarding take?",
        a: "Most sellers go from signup to first paid invoice in under an hour. You upload basic KYB documents (EIN, articles of incorporation, bank info) and you're ready to fund invoices the same day.",
      },
    ],
  },
  {
    label: "For buyers",
    items: [
      {
        q: "Does it really cost $0 to use verqo as a buyer?",
        a: "The verqo platform is free for buyers — no subscription, no application fees, no underwriting fees, no credit-line fees. Suppliers cover our cost. You're still responsible for the normal pass-through costs of doing business (wire fees, card processing, shipping, and any dropship or fulfillment fees), and we don't mark those up.",
      },
      {
        q: "How fast is the credit decision?",
        a: "Under 60 seconds. You connect your bank via Plaid, we verify your EIN, and our AI underwriter drafts your credit memo and approves your line in real time. No paper trade references. No multi-week waits.",
      },
      {
        q: "What if my suppliers aren't on verqo yet?",
        a: "Two options. (1) You can still get pre-approved and use the proof to negotiate better terms with off-network suppliers. (2) Use our PO Generator to send your supplier a verqo-issued PO with the payment terms you want — they onboard in minutes to fulfill it, and you become their preferred buyer.",
      },
      {
        q: "Can I use my credit line at any supplier on verqo?",
        a: "Yes. Apply once, use it at every supplier on the network. Each seller sets the maximum term they offer (Net-30, 60, or 90), and your line + payment history is honored across all of them. The better your record, the better the terms you unlock.",
      },
      {
        q: "What categories of suppliers are on verqo?",
        a: "Fashion & apparel, beauty, food & beverage, home goods, pet, wellness, luxury goods, and tech accessories. We focus on independent and emerging wholesale brands doing $1M–$100M — the brands your customers actually want.",
      },
    ],
  },
  {
    label: "Getting started",
    items: [
      {
        q: "Who is verqo for?",
        a: "verqo is built for wholesale brands ($1M-$100M GMV) selling to retailers, independent shops, distributors and online resellers. Sellers come from beauty, fashion, food & beverage, health, household and pet categories. Buyers can be any business that buys wholesale.",
      },
      {
        q: "Do I need to replace my existing tools?",
        a: "Only if you want to. verqo plugs into Shopify, NetSuite, QuickBooks Online and standard accounting + ERP stacks. Many sellers run verqo alongside their existing systems for the first 60 days before consolidating.",
      },
      {
        q: "Is there a free trial?",
        a: "Yes — you can sign up at no cost and explore every module. You only pay your monthly plan once you're ready to take live orders and process payments. Buyers always use verqo for free.",
      },
    ],
  },
  {
    label: "Pricing & fees",
    items: [
      {
        q: "What does it cost?",
        a: "Sellers pay a flat monthly plan ($99 Starter · $599 Growth · $1,499 Pro · $3,999 Enterprise) and a small transaction fee only on the flows we actually move (e.g. 3% on same-day invoice financing, pass-through cost on card/ACH/wire). Buyers pay $0 to verqo — always.",
      },
      {
        q: "Do you charge per invoice?",
        a: "No. There is no per-invoice fee, no per-PO fee, no setup fee, no minimum volume. You can run unlimited orders, invoices and buyers under any plan.",
      },
      {
        q: "What about hidden fees?",
        a: "There aren't any. We publish every transaction rate (ACH, wire, card, instant payout, factoring) on the pricing page. If a flow moves through verqo, you'll see the fee before you confirm. If a fee isn't listed, it doesn't exist.",
      },
      {
        q: "Why do buyers pay $0?",
        a: "Because demand is the engine of the network. Buyers being free means more orders, which means more value for sellers, which means a flywheel that's healthy for everyone. Sellers cover the platform cost because they're the ones getting the cash-flow lift.",
      },
    ],
  },
  {
    label: "Credit, payments & settlement",
    items: [
      {
        q: "How does the credit decision work?",
        a: "We score every buyer in seconds using cash-flow data, sales velocity, payment history, and business-state signals. The verqo Buyer Score (0-850) decides whether Net-30, Net-60 or Net-90 is offered, and at what limit. No paperwork, no manual review.",
      },
      {
        q: "Who funds the credit?",
        a: "verqo isn't the lender — we route every credit line to an institutional funder (bank, fintech lender, or specialty lender) and add credit insurance via our underwriting partner. Sellers get paid by us on day 0; the lender carries the float.",
      },
      {
        q: "Can I get paid the same day I ship?",
        a: "Yes. With Instant Payout, you receive funds the same day the invoice goes out — typically within minutes. The cost is a flat percentage shown at the time of factoring. No reserves, no recourse, no personal guarantees.",
      },
      {
        q: "What payment methods do my buyers have?",
        a: "ACH, wire, card (debit and credit), wallet balance, and bank-direct settlement. Buyers can also use their verqo credit line to pay later on Net-30/60/90 terms.",
      },
    ],
  },
  {
    label: "Trust, compliance & security",
    items: [
      {
        q: "Is verqo a bank?",
        a: "No — and we don't pretend to be. We partner with a chartered sponsor bank (U.S. Bank) for FBO accounts and money movement. Customer funds are held in segregated FBO sub-ledgers, not commingled with verqo operating capital.",
      },
      {
        q: "What about AML, KYC and sanctions?",
        a: "Every transaction passes through a 16-rule AML engine, live OFAC SDN screening, and an 8-step policy gate before it can move. Bank examiners can review every flow through our examiner portal in real time.",
      },
      {
        q: "Who insures the trade?",
        a: "Allianz Trade is our credit insurance partner. Every approved invoice is covered, which is what lets us extend Net-30/60/90 without forcing personal guarantees on buyers.",
      },
      {
        q: "Is my financial data secure?",
        a: "Bank-grade. We use Plaid for read-only bank connections, never store credentials, and are SOC 2 Type II audited. Your data is encrypted in transit and at rest. Customer PII is encrypted and all admin reads of sensitive fields are written to a tamper-evident hash chain.",
      },
    ],
  },
];

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200" data-testid={testid}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left py-5 flex items-start justify-between gap-6 hover:text-slate-900 transition-colors"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <span className="text-[15px] md:text-[16px] font-medium text-slate-900 pr-4">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 mt-1.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="pb-5 -mt-2 text-[14px] md:text-[14.5px] leading-relaxed text-slate-600 max-w-[800px]">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  useEffect(() => { document.title = "FAQ · verqo"; window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#f6f7f5] text-slate-900">
      <header className="px-6 md:px-12 max-w-[1280px] mx-auto pt-8 pb-4 flex items-center justify-between" data-testid="faq-header">
        <Link to="/" className="inline-flex items-center"><VerqoLockup size="sm" /></Link>
        <nav className="hidden lg:flex items-center gap-6 text-[12px] text-slate-600">
          <Link to="/" className="hover:text-slate-900">For sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">For buyers</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </nav>
        <MarketingMobileNav />
      </header>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pt-16 md:pt-24 pb-12" data-testid="faq-hero">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.25em] text-lime-700 mb-5">
          <span className="w-2 h-2 rounded-full bg-lime-500"/>FAQ
        </div>
        <h1 className="text-[40px] md:text-[60px] leading-[0.98] tracking-tight font-semibold">
          The straight answers <br/><span className="text-slate-500">we wish someone gave us.</span>
        </h1>
        <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
          Everything you'd ask before you signed up — pricing, credit, settlement,
          compliance, security. No marketing fluff.
        </p>
      </section>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pb-24" data-testid="faq-list">
        {SECTIONS.map((sec, si) => (
          <div key={si} className="mb-14" data-testid={`faq-section-${si}`}>
            <div className="mb-6 text-[10.5px] uppercase tracking-[0.22em] text-slate-500">{sec.label}</div>
            <div>
              {sec.items.map((it, ii) => (
                <FaqItem key={ii} q={it.q} a={it.a} testid={`faq-item-${si}-${ii}`} />
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pb-24" data-testid="faq-cta">
        <div className="rounded-2xl bg-slate-900 text-white p-8 md:p-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="text-[18px] md:text-[22px] font-semibold tracking-tight">Still have a question?</div>
            <div className="mt-1 text-[13.5px] text-white/70">We answer every email — usually within a few hours.</div>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-lime-300 text-slate-900 text-[13.5px] font-semibold hover:bg-lime-200 transition-colors" data-testid="faq-contact-cta">
            Talk to us <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>

      <footer className="px-6 md:px-12 max-w-[1280px] mx-auto py-10 border-t border-slate-200 flex flex-wrap gap-4 items-center justify-between text-[11px] text-slate-500" data-testid="faq-footer">
        <div className="flex items-center gap-3"><VerqoLockup size="sm" /><span>· Wholesale, instant.</span></div>
        <div className="flex items-center gap-5">
          <Link to="/" className="hover:text-slate-900">Sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">Buyers</Link>
          <Link to="/about" className="hover:text-slate-900">About</Link>
          <Link to="/faq" className="hover:text-slate-900">FAQ</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </div>
        <div>© 2026 verqo</div>
      </footer>
    </div>
  );
}
